import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Box } from "@mui/material";

import { useAuthContext, useSearchCustomersContext } from "../../context";
import TableBody from "./components/TableBody";
import TableHeader from "./components/TableHeader";
import { setPage } from "../../utils";
import {
  ContainerWithLoader,
  DateRangePicker,
  PaginationWithLimit,
  StatusColorLegend,
} from "../../components/ui";
import ViewTableControlsAndActions from "./components/ViewTableControlsAndActions";
import CustomersFilters from "./components/CustomersFilters";
import { getCustomer } from "./utils";
import { StyledTable } from "../../useGeneralStyles";
import { STATUSES_COLORS } from "./constants";
import { DATE_FORMAT } from "../../constants";

const Customers = (): React.ReactElement => {
  const searchCustomersContext = useSearchCustomersContext();
  const {
    search,
    page,
    setSearch,
    limit,
    sortBy,
    isShowActiveSubscription,
    isShowLeads,
    setLimit,
    setPage: setPageContext,
    relatedTables,
    isShowInactiveAccounts,
    isShowPassedEvaluations,
    isShowBanned,
    isShowAbandoned,
  } = searchCustomersContext;

  const { loginAndQuery, hasExpired } = useAuthContext();
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.customers.data);
  const customerCount = useSelector((state) => state.customers.data.count);
  const { loading, data } = useSelector((state) => state.customers);

  const [key, setKey] = useState(1);

  const handleSetPage = (value: number) => setPage(value, customerCount, +limit, setPageContext);

  useEffect(() => {
    setSearch(search);
    const controller = new AbortController();
    const { signal } = controller;

    loginAndQuery(
      // @ts-ignore
      getCustomer.bind(
        null,
        searchCustomersContext,
        customerCount,
        hasExpired,
        customers,
        false,
        dispatch,
        signal,
        false
      )
    );
    return () => {
      controller.abort();
    };
  }, [
    JSON.stringify(search),
    limit,
    isShowLeads,
    isShowActiveSubscription,
    isShowInactiveAccounts,
    isShowPassedEvaluations,
    isShowBanned,
    isShowAbandoned,
    relatedTables,
    page,
    sortBy,
  ]);

  return (
    <div className="customers">
      <ViewTableControlsAndActions key={key} keyVal={key} setKey={setKey} />
      <CustomersFilters keyVal={key} />

      <Grid container spacing={2}>
        <DateRangePicker
          startDate={search.joined_after || null}
          endDate={search.joined_before || null}
          label="Joined"
          onChange={(date, inputName) => {
            setSearch({
              ...search,
              ["joined_" + inputName]: date ? date.format(DATE_FORMAT) : null,
            });
          }}
          onClear={() => {
            setSearch({
              ...search,
              joined_after: null,
              joined_before: null,
            });
          }}
        />

        <DateRangePicker
          startDate={search.expires_after || null}
          endDate={search.expires_before || null}
          label="Expires"
          onChange={(date, inputName) => {
            setSearch({
              ...search,
              ["expires_" + inputName]: date ? date.format(DATE_FORMAT) : null,
            });
          }}
          onClear={() => {
            setSearch({
              ...search,
              expires_after: null,
              expires_before: null,
            });
          }}
        />
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb="5px" mt="5px">
        <p style={{ margin: "5px 0" }}>
          Users found:<b>{customers.count}</b>
        </p>
        <StatusColorLegend legend={STATUSES_COLORS} />
      </Box>

      <Box sx={{ overflowX: "scroll" }}>
        <ContainerWithLoader isLoading={loading} report={data.accounts}>
          <StyledTable sx={{ "& tr:nth-of-type(even) td": { backgroundColor: "inherit" } }}>
            <TableHeader />
            <TableBody />
          </StyledTable>
        </ContainerWithLoader>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb="5px" mt="5px">
        <PaginationWithLimit
          count={customerCount}
          limit={limit}
          page={page}
          setPageState={handleSetPage}
          setLimit={setLimit}
        />
      </Box>
    </div>
  );
};

export default Customers;
